// useDraftAutosave.js - Automatisk sparning av journalutkast (Spår J)
//
// Utkast sparas automatiskt så att osparad text aldrig försvinner tyst när man
// byter journal, flik, går till kalendern, betalar besöket eller stänger
// webbläsarfliken. Aldrig automatisk signering — bara innehållet sparas.
//
// - Debounce: sparar AUTOSAVE_DELAY_MS efter senaste ändringen.
// - Direkt sparning vid blur (fält/fönster), visibilitychange (hidden),
//   pagehide/beforeunload och avmontering. Sparningar när sidan kan vara på väg
//   bort görs med fetch(…, {keepalive: true}) + Bearer-token (sendBeacon kan
//   inte skicka headers).
// - Optimistisk låsning: PUT skickar expected_version, 409 = någon annan har
//   sparat emellan → autosparen stoppas och användaren får välja.
// - Pågående sparningar registreras per post (waitForPendingSave) så att en
//   ny hämtning av samma post (t.ex. direkt efter avmontering) inte läser
//   gammalt innehåll.

import { useState, useEffect, useRef, useCallback } from 'react';
import apiClient from '../../apiClient';
import { isJournalContentEmpty } from './journalApi';

export const AUTOSAVE_DELAY_MS = 2500;
const RETRY_DELAY_MS = 10000;
// keepalive-förfrågningar får tillsammans max 64 KiB body i webbläsaren
const KEEPALIVE_MAX_BYTES = 60000;

// entryId -> Promise för pågående sparning (modulnivå: överlever avmontering)
const pendingSaves = new Map();

/** Vänta in ev. pågående autosparning för posten innan den hämtas på nytt. */
export const waitForPendingSave = async (entryId) => {
  const pending = pendingSaves.get(entryId);
  if (pending) {
    try { await pending; } catch (e) { /* felet hanteras av hooken */ }
  }
};

const trackPending = (entryId, promise) => {
  pendingSaves.set(entryId, promise);
  const clear = () => {
    if (pendingSaves.get(entryId) === promise) pendingSaves.delete(entryId);
  };
  promise.then(clear, clear);
  return promise;
};

const putUrl = (entryId) => `/api/journal/entries/${entryId}`;

// Returnerar alltid {status, data} — kastar bara vid nätverksfel.
const sendSave = async (entryId, body, keepalive) => {
  const json = JSON.stringify(body);
  // Kvoten gäller byte (UTF-8), inte tecken — å/ä/ö är två byte
  const bytes = typeof Blob === 'function' ? new Blob([json]).size : json.length * 3;
  if (keepalive && typeof fetch === 'function' && bytes < KEEPALIVE_MAX_BYTES) {
    const token = localStorage.getItem('access_token');
    const resp = await fetch(`${apiClient.defaults.baseURL || ''}${putUrl(entryId)}`, {
      method: 'PUT',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: json,
    });
    let data = null;
    try { data = await resp.json(); } catch (e) { /* tomt svar */ }
    return { status: resp.status, data };
  }
  try {
    const resp = await apiClient.put(putUrl(entryId), body);
    return { status: resp.status, data: resp.data };
  } catch (err) {
    if (err.response) return { status: err.response.status, data: err.response.data };
    throw err;
  }
};

const EMPTY = { sections: [] };

/**
 * @param {object} opts
 * @param {number} opts.entryId
 * @param {boolean} opts.enabled - redigerar ett utkast
 * @param {boolean} opts.paused - t.ex. under diktering (processing/generating)
 * @param {object} opts.content - aktuellt editorinnehåll
 * @param {object} opts.serverContent - postens innehåll enligt servern (journal.content)
 * @param {number} opts.serverVersion - postens version enligt servern
 * @param {function} opts.onSaved - ({content, version, updated_at}) efter lyckad sparning
 * @param {function} opts.onSignedElsewhere - posten har signerats någon annanstans
 */
export default function useDraftAutosave({
  entryId,
  enabled,
  paused = false,
  content,
  serverContent,
  serverVersion,
  onSaved,
  onSignedElsewhere,
}) {
  // idle | dirty | saving | saved | error | conflict | signed
  const [status, setStatus] = useState('idle');
  const [lastSavedAt, setLastSavedAt] = useState(null);

  const contentRef = useRef(content);
  const lastSavedJsonRef = useRef(JSON.stringify(serverContent || EMPTY));
  const versionRef = useRef(serverVersion || 1);
  const inflightRef = useRef(null);
  const inflightJsonRef = useRef(null);
  const timerRef = useRef(null);
  const stoppedRef = useRef(false);   // konflikt eller signerad — ingen autospar
  const suspendedRef = useRef(false); // under signering
  const enabledRef = useRef(enabled);
  const pausedRef = useRef(paused);
  const mountedRef = useRef(true);
  const onSavedRef = useRef(onSaved);
  const onSignedRef = useRef(onSignedElsewhere);
  const saveRef = useRef(null);

  contentRef.current = content;
  enabledRef.current = enabled;
  pausedRef.current = paused;
  onSavedRef.current = onSaved;
  onSignedRef.current = onSignedElsewhere;

  // Utanför redigering följer baslinjen serverns version (t.ex. efter
  // omhämtning). Under redigering äger hooken baslinjen — annars kunde en
  // omhämtning (betalning m.m.) ge falsk konflikt eller falskt "sparat".
  useEffect(() => {
    if (enabled) return;
    lastSavedJsonRef.current = JSON.stringify(serverContent || EMPTY);
    versionRef.current = serverVersion || 1;
    stoppedRef.current = false;
    setStatus('idle');
  }, [enabled, serverContent, serverVersion]);

  const setStatusSafe = (s) => { if (mountedRef.current) setStatus(s); };

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const isDirtyNow = () => JSON.stringify(contentRef.current || EMPTY) !== lastSavedJsonRef.current;

  const schedule = useCallback((delay = AUTOSAVE_DELAY_MS) => {
    clearTimer();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      if (saveRef.current) saveRef.current();
    }, delay);
  }, []);

  /**
   * Spara aktuellt innehåll om det skiljer sig från senast sparade.
   * opts.keepalive — sidan kan vara på väg bort (fetch keepalive)
   * opts.force     — spara även om autosparen är pausad (sista sparningen
   *                  före diktering)
   */
  const save = useCallback(async ({ keepalive = false, force = false } = {}) => {
    if (!enabledRef.current || stoppedRef.current) return;
    if ((pausedRef.current || suspendedRef.current) && !force) return;
    clearTimer();

    const current = contentRef.current || EMPTY;
    const json = JSON.stringify(current);
    if (json === lastSavedJsonRef.current) return;
    if (inflightRef.current && json === inflightJsonRef.current) {
      await inflightRef.current;
      return;
    }
    // Tomskydd: autospara inte tomt innehåll om servern också är tom.
    // (En avsiktlig tömning av en post med text sparas däremot.)
    if (isJournalContentEmpty(current) &&
        isJournalContentEmpty(JSON.parse(lastSavedJsonRef.current))) {
      return;
    }

    if (inflightRef.current) {
      if (keepalive) {
        // Sidan kan försvinna innan pågående sparning är klar — skicka direkt
        // och anta att den pågående lyckas (servern serialiserar via radlås).
        const p = sendSave(entryId,
          { content: current, expected_version: versionRef.current + 1, autosave: true }, true)
          .then(({ status: st, data }) => {
            if (st >= 200 && st < 300 && data) {
              if ((data.version || 0) >= versionRef.current) {
                versionRef.current = data.version;
                lastSavedJsonRef.current = json;
              }
            } else if (st === 409) {
              // Lever sidan kvar ska användaren se att texten inte sparades
              stoppedRef.current = true;
              setStatusSafe('conflict');
            }
          })
          .catch(() => { setStatusSafe('error'); });
        trackPending(entryId, p);
        return;
      }
      try { await inflightRef.current; } catch (e) { /* hanteras nedan */ }
      return saveRef.current && saveRef.current({ keepalive, force });
    }

    setStatusSafe('saving');
    inflightJsonRef.current = json;
    const body = { content: current, expected_version: versionRef.current, autosave: true };
    const run = (async () => {
      let result;
      try {
        result = await sendSave(entryId, body, keepalive);
      } catch (err) {
        console.error('Autospar misslyckades (nätverk):', err);
        setStatusSafe('error');
        if (mountedRef.current && enabledRef.current) schedule(RETRY_DELAY_MS);
        return;
      }
      const { status: st, data } = result;
      if (st >= 200 && st < 300) {
        const newVersion = data?.version || versionRef.current + 1;
        // En keepalive-sparning kan ha hunnit före med nyare innehåll — gå
        // aldrig bakåt i version/baslinje (gav annars falsk konflikt).
        if (newVersion >= versionRef.current) {
          versionRef.current = newVersion;
          lastSavedJsonRef.current = json;
        }
        if (mountedRef.current) setLastSavedAt(new Date());
        if (onSavedRef.current) {
          onSavedRef.current({ content: current, version: versionRef.current, updated_at: data?.updated_at });
        }
        if (isDirtyNow()) {
          setStatusSafe('dirty');
          if (mountedRef.current && enabledRef.current && !pausedRef.current && !suspendedRef.current) {
            schedule();
          }
        } else {
          setStatusSafe('saved');
        }
      } else if (st === 409) {
        stoppedRef.current = true;
        setStatusSafe('conflict');
      } else if (st === 403 && data?.signed) {
        stoppedRef.current = true;
        setStatusSafe('signed');
        if (onSignedRef.current) onSignedRef.current();
      } else {
        console.error('Autospar misslyckades:', st, data);
        setStatusSafe('error');
        if (mountedRef.current && enabledRef.current) schedule(RETRY_DELAY_MS);
      }
    })();
    inflightRef.current = run;
    trackPending(entryId, run);
    try {
      await run;
    } finally {
      if (inflightRef.current === run) {
        inflightRef.current = null;
        inflightJsonRef.current = null;
      }
    }
  }, [entryId, schedule]);
  saveRef.current = save;

  // Debounce vid varje ändring
  useEffect(() => {
    if (!enabled || stoppedRef.current) return;
    if (!isDirtyNow()) {
      clearTimer();
      setStatus((s) => (s === 'dirty' ? (lastSavedAt ? 'saved' : 'idle') : s));
      return;
    }
    setStatus((s) => (s === 'saving' ? s : 'dirty'));
    if (!paused && !suspendedRef.current) schedule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, enabled, paused, schedule]);

  // Direkt sparning när sidan döljs/lämnas eller fönstret tappar fokus
  useEffect(() => {
    if (!enabled) return undefined;
    const onHidden = () => {
      if (document.visibilityState === 'hidden') save({ keepalive: true });
    };
    const onPageHide = () => save({ keepalive: true });
    const onWindowBlur = () => save();
    const onBeforeUnload = (e) => {
      save({ keepalive: true });
      // Kan vi inte autospara (konflikt/fel/diktering) — varna som förr
      if ((stoppedRef.current || pausedRef.current) && isDirtyNow()) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
      return undefined;
    };
    document.addEventListener('visibilitychange', onHidden);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('blur', onWindowBlur);
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      document.removeEventListener('visibilitychange', onHidden);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('blur', onWindowBlur);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [enabled, save]);

  // Avmontering (byte av journal/flik, kalendern …): spara direkt
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimer();
      if (saveRef.current) saveRef.current({ keepalive: true });
    };
  }, []);

  /** Spara direkt (avbryt debounce) och vänta in. True om allt är sparat. */
  const flush = useCallback(async ({ force = false } = {}) => {
    clearTimer();
    if (inflightRef.current) {
      try { await inflightRef.current; } catch (e) { /* hanteras i save */ }
    }
    await save({ force });
    if (inflightRef.current) {
      try { await inflightRef.current; } catch (e) { /* hanteras i save */ }
    }
    return !stoppedRef.current && !isDirtyNow();
  }, [save]);

  /** Pausa autosparen (t.ex. under signering) och vänta in pågående sparning. */
  const suspend = useCallback(async () => {
    suspendedRef.current = true;
    clearTimer();
    if (inflightRef.current) {
      try { await inflightRef.current; } catch (e) { /* hanteras i save */ }
    }
  }, []);

  const resume = useCallback(() => {
    suspendedRef.current = false;
    if (enabledRef.current && !stoppedRef.current && isDirtyNow()) schedule();
  }, [schedule]);

  /** Innehållet har sparats utanför hooken (t.ex. diktering via kontextet). */
  const markSaved = useCallback((savedContent, version) => {
    clearTimer();
    lastSavedJsonRef.current = JSON.stringify(savedContent || EMPTY);
    if (version) versionRef.current = version;
    stoppedRef.current = false;
    setLastSavedAt(new Date());
    setStatus('saved');
  }, []);

  /** Konflikt: ta serverns version (anroparen sätter editorinnehållet). */
  const acceptServer = useCallback((srvContent, version) => {
    clearTimer();
    lastSavedJsonRef.current = JSON.stringify(srvContent || EMPTY);
    versionRef.current = version || 1;
    stoppedRef.current = false;
    setStatus('saved');
    setLastSavedAt(new Date());
  }, []);

  /** Konflikt: behåll mitt innehåll och skriv över serverns version. */
  const keepMine = useCallback(async (version) => {
    versionRef.current = version || versionRef.current;
    stoppedRef.current = false;
    await save();
    return !stoppedRef.current && !isDirtyNow();
  }, [save]);

  /** Signeringen fick 409 — visa konfliktdialogen och stoppa autosparen. */
  const markConflict = useCallback(() => {
    clearTimer();
    stoppedRef.current = true;
    setStatus('conflict');
  }, []);

  const getVersion = useCallback(() => versionRef.current, []);

  /** Spara direkt utan att vänta (t.ex. när ett fält tappar fokus). */
  const saveNow = useCallback(() => { save(); }, [save]);

  return {
    status, lastSavedAt, saveNow, flush, suspend, resume,
    markSaved, markConflict, acceptServer, keepMine, getVersion,
  };
}
